/**
 * @module svg/scenes
 *
 * Large illustrative SVG scenes — the "3D-SVG" building blocks of the site.
 * Each is flat vector geometry given the *appearance* of depth through elliptical
 * projection, layered translucent planes, gradients, glow filters and isometric
 * offsets. GSAP then animates them (draw-on strokes, orbital rotation, parallax).
 *
 * Every scene is a pure function of its options and returns an SVG markup
 * string, which makes them trivial to unit-test and to render in isolation.
 */

import { escapeAttribute, GLYPH_DATA } from "./icons.js";

/**
 * Monotonic counter guaranteeing unique gradient/filter ids per instance.
 * Duplicate ids across inline SVGs cause gradient cross-talk between scenes.
 */
let uid = 0;

/**
 * Generate a namespaced, unique id.
 *
 * @param {string} [prefix="svg"]
 * @returns {string}
 */
export function uniqueId(prefix = "svg") {
  uid += 1;
  return `${prefix}-${uid}`;
}

/**
 * Deterministic pseudo-random — keeps generated geometry stable between renders
 * so snapshots and visual regressions are meaningful.
 *
 * @param {number} n
 * @returns {number} A value in [0, 1).
 */
function jitter(n) {
  const x = Math.sin(n * 127.1) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Convert polar coordinates to cartesian.
 *
 * @param {number} cx
 * @param {number} cy
 * @param {number} r
 * @param {number} angleDeg
 * @returns {{x: number, y: number}}
 */
function polar(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/**
 * Build an SVG arc path between two angles.
 *
 * @param {number} cx
 * @param {number} cy
 * @param {number} r
 * @param {number} startDeg
 * @param {number} endDeg
 * @returns {string} Path `d` attribute.
 */
export function arcPath(cx, cy, r, startDeg, endDeg) {
  const start = polar(cx, cy, r, startDeg);
  const end = polar(cx, cy, r, endDeg);
  const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  const sweep = endDeg > startDeg ? 1 : 0;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${r} ${r} 0 ${large} ${sweep} ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

/**
 * Build a regular polygon path.
 *
 * @param {number} cx
 * @param {number} cy
 * @param {number} r   Circumradius.
 * @param {number} [sides=6]
 * @param {number} [rotation=0] Degrees.
 * @returns {string} Path `d` attribute.
 */
export function polygonPath(cx, cy, r, sides = 6, rotation = 0) {
  const points = Array.from({ length: sides }, (_, i) => {
    const point = polar(cx, cy, r, (360 / sides) * i + rotation);
    return `${point.x.toFixed(2)},${point.y.toFixed(2)}`;
  });
  return `M${points.join("L")}Z`;
}

/**
 * Shared `<defs>` for a scene: gradients, glow filter and an arrow marker.
 *
 * @param {string} id        Base id.
 * @param {string} accent    Dominant accent.
 * @param {string} secondary Secondary accent.
 * @returns {string} `<defs>` markup.
 */
function sceneDefs(id, accent, secondary) {
  return `
  <defs>
    <radialGradient id="${id}-core" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.92"/>
      <stop offset="34%" stop-color="${accent}" stop-opacity="0.8"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="${id}-halo" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="${secondary}" stop-opacity="0.4"/>
      <stop offset="100%" stop-color="${secondary}" stop-opacity="0"/>
    </radialGradient>
    <linearGradient id="${id}-stroke" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${accent}" stop-opacity="0.95"/>
      <stop offset="52%" stop-color="${secondary}" stop-opacity="0.6"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0.16"/>
    </linearGradient>
    <linearGradient id="${id}-sheen" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.14"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </linearGradient>
    <filter id="${id}-glow" x="-70%" y="-70%" width="240%" height="240%">
      <feGaussianBlur stdDeviation="6" result="blur"/>
      <feMerge>
        <feMergeNode in="blur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
    <filter id="${id}-soft" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="3"/>
    </filter>
    <marker id="${id}-arrow" viewBox="0 0 10 10" refX="8" refY="5"
            markerWidth="5" markerHeight="5" orient="auto-start-reverse">
      <path d="M0 0 10 5 0 10z" fill="${accent}" fill-opacity="0.8"/>
    </marker>
  </defs>`;
}

/* ========================================================================== */
/* 1. Hero — the ADM wheel                                                    */
/* ========================================================================== */

/**
 * The hero scene: the TOGAF ADM drawn as an instrument.
 *
 * Nine phase arcs sit on a calibrated ring. The **Requirements Management** hub
 * glows at the centre, feeding every phase. A swarm of AI dots drifts through
 * the orbit — the distributed intelligence the method exists to place.
 *
 * GSAP hooks (class names):
 *  - `.wheel-ticks`   calibration ring (slow rotation)
 *  - `.wheel-sweep`   sweeping indicator arc
 *  - `.wheel-orbit`   each ring, drawn on with DrawSVG
 *  - `.wheel-arc`     the nine phase arcs
 *  - `.wheel-code`    the phase letter labels
 *  - `.wheel-swarm`   the rotating AI dot swarm
 *  - `.wheel-dot`     individual AI dots
 *  - `.wheel-core`    the glowing centre
 *  - `.wheel-hub`     the requirements hub ring
 *
 * @param {Object} [options]
 * @param {string} [options.accent="#5fd4c4"]
 * @param {string} [options.secondary="#d8a657"]
 * @param {string} [options.tertiary="#a98bc4"]
 * @param {number} [options.size=560]
 * @returns {string} SVG markup.
 */
export function admWheel(options = {}) {
  const {
    accent = "#5fd4c4",
    secondary = "#d8a657",
    tertiary = "#a98bc4",
    size = 560,
  } = options;

  const id = uniqueId("wheel");
  const c = size / 2;

  // Calibration ticks around the outer ring.
  const ticks = Array.from({ length: 72 }, (_, i) => {
    const a = (i / 72) * 360;
    const major = i % 6 === 0;
    const outer = 252;
    const inner = major ? 236 : 244;
    const p1 = polar(c, c, inner, a);
    const p2 = polar(c, c, outer, a);
    return `<line x1="${p1.x.toFixed(1)}" y1="${p1.y.toFixed(1)}" x2="${p2.x.toFixed(1)}" y2="${p2.y.toFixed(1)}"
              stroke="${accent}" stroke-width="${major ? 1.3 : 0.6}" opacity="${major ? 0.75 : 0.32}"/>`;
  }).join("");

  // Nine phase arcs on a 218-radius ring, with a 3° gap between them.
  const arcRadius = 218;
  const gap = 4;
  const segments = 9;
  const step = 360 / segments;
  const arcs = Array.from({ length: segments }, (_, i) => {
    const start = i * step + gap / 2;
    const end = (i + 1) * step - gap / 2;
    const code = ["P", "A", "B", "C", "D", "E", "F", "G", "H"][i];
    const mid = start + (end - start) / 2;
    const label = polar(c, c, arcRadius - 34, mid);
    const isHub = i === 0;
    return `
      <path class="wheel-arc" data-phase-code="${code}" d="${arcPath(c, c, arcRadius, start, end)}"
            fill="none" stroke="${isHub ? secondary : accent}" stroke-width="${isHub ? 3.2 : 2.4}"
            stroke-linecap="round" opacity="${0.5 + (i / segments) * 0.45}"/>
      <text class="wheel-code" data-phase-code="${code}" x="${label.x.toFixed(1)}" y="${label.y.toFixed(1)}"
            text-anchor="middle" dominant-baseline="central"
            font-size="19" font-weight="600" letter-spacing="0.5"
            fill="${isHub ? secondary : "#f2efe9"}" opacity="0.9">${code}</text>`;
  }).join("");

  // AI dot swarm.
  const dots = Array.from({ length: 34 }, (_, i) => {
    const angle = (i / 34) * 360 + jitter(i) * 22;
    const radius = 96 + jitter(i + 11) * 108;
    const p = polar(c, c, radius, angle);
    const r = 1.1 + jitter(i + 29) * 2.2;
    const fill = jitter(i + 5) > 0.68 ? tertiary : accent;
    return `<circle class="wheel-dot" cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="${r.toFixed(2)}"
              fill="${fill}" opacity="${(0.3 + jitter(i + 3) * 0.55).toFixed(2)}"/>`;
  }).join("");

  return `
<svg class="scene scene--wheel" viewBox="0 0 ${size} ${size}" role="img"
     aria-label="The TOGAF Architecture Development Method drawn as a calibrated instrument: nine phase arcs encircling the Requirements Management hub, with AI points distributed through the orbit.">
  ${sceneDefs(id, accent, secondary)}

  <!-- Calibration ring -->
  <g class="wheel-ticks" style="transform-origin:${c}px ${c}px" opacity="0.5">${ticks}</g>

  <!-- Outer instrument circles -->
  <g class="wheel-rings" fill="none">
    <circle class="wheel-orbit" cx="${c}" cy="${c}" r="252" stroke="${accent}" stroke-width="0.8" opacity="0.24"/>
    <ellipse class="wheel-orbit" cx="${c}" cy="${c}" rx="246" ry="196" stroke="url(#${id}-stroke)" stroke-width="1.5" opacity="0.7" transform="rotate(-16 ${c} ${c})"/>
    <ellipse class="wheel-orbit" cx="${c}" cy="${c}" rx="206" ry="248" stroke="${tertiary}" stroke-width="1" opacity="0.34" transform="rotate(24 ${c} ${c})"/>
  </g>

  <!-- Phase arcs + codes -->
  <g class="wheel-arcs">${arcs}</g>

  <!-- Sweeping indicator -->
  <g class="wheel-sweep" style="transform-origin:${c}px ${c}px">
    <path d="${arcPath(c, c, 234, 0, 42)}" fill="none" stroke="url(#${id}-stroke)"
          stroke-width="2" stroke-linecap="round" opacity="0.85"/>
    <circle cx="${polar(c, c, 234, 42).x.toFixed(1)}" cy="${polar(c, c, 234, 42).y.toFixed(1)}"
            r="3" fill="${accent}"/>
  </g>

  <!-- Inner orbit rings -->
  <g class="wheel-inner" fill="none">
    <circle cx="${c}" cy="${c}" r="168" stroke="${accent}" stroke-width="0.7" opacity="0.2" stroke-dasharray="2 9"/>
    <circle cx="${c}" cy="${c}" r="132" stroke="${secondary}" stroke-width="0.7" opacity="0.18"/>
  </g>

  <!-- AI dot swarm -->
  <g class="wheel-swarm" style="transform-origin:${c}px ${c}px">${dots}</g>

  <!-- Requirements Management hub -->
  <g class="wheel-hub" style="transform-origin:${c}px ${c}px">
    <circle cx="${c}" cy="${c}" r="66" fill="none" stroke="${accent}" stroke-width="1.2" opacity="0.6"/>
    <circle cx="${c}" cy="${c}" r="54" fill="none" stroke="${accent}" stroke-width="0.8" opacity="0.35" stroke-dasharray="3 6"/>
    <path d="${polygonPath(c, c, 44, 6, 0)}" fill="none" stroke="url(#${id}-stroke)" stroke-width="1.8" opacity="0.9"/>
    <path d="${polygonPath(c, c, 30, 6, 30)}" fill="none" stroke="${secondary}" stroke-width="1" opacity="0.6"/>
  </g>

  <!-- Glowing core -->
  <g class="wheel-core" style="transform-origin:${c}px ${c}px">
    <circle cx="${c}" cy="${c}" r="86" fill="url(#${id}-core)" opacity="0.6"/>
    <circle cx="${c}" cy="${c}" r="6.5" fill="#ffffff" filter="url(#${id}-glow)"/>
  </g>

  <!-- Depth sheen -->
  <ellipse cx="${c}" cy="${c - 4}" rx="236" ry="228" fill="url(#${id}-sheen)" opacity="0.28" pointer-events="none"/>
</svg>`.trim();
}

/* ========================================================================== */
/* 2. The interactive ADM orbit (phase navigator)                             */
/* ========================================================================== */

/**
 * The 2.5D ring behind the interactive phase navigator.
 *
 * The ring is drawn as a tilted ellipse using an isometric-style projection, so
 * flat geometry reads as a three-dimensional orbit. The phase nodes themselves
 * are **not** part of this markup — they are real, focusable buttons positioned
 * by `components/admOrbit.js` so they stay keyboard accessible.
 *
 * @param {Object} [options]
 * @param {string} [options.accent="#5fd4c4"]
 * @param {string} [options.secondary="#d8a657"]
 * @param {number} [options.width=980]
 * @param {number} [options.height=560]
 * @param {number} [options.spokes=18]
 * @returns {string} SVG markup.
 */
export function admOrbit(options = {}) {
  const {
    accent = "#5fd4c4",
    secondary = "#d8a657",
    width = 980,
    height = 560,
    spokes = 18,
  } = options;

  const id = uniqueId("orbit");
  const cx = width / 2;
  const cy = height / 2 - 6;
  const rx = 386;
  const ry = 150;

  const spokeLines = Array.from({ length: spokes }, (_, i) => {
    const a = (i / spokes) * 360;
    const outer = polar(cx, cy, 1, a);
    const p1 = { x: cx + (outer.x - cx) * rx, y: cy + (outer.y - cy) * ry };
    const p2 = { x: cx + (outer.x - cx) * (rx - 34), y: cy + (outer.y - cy) * (ry - 13) };
    return `<line x1="${p1.x.toFixed(1)}" y1="${p1.y.toFixed(1)}" x2="${p2.x.toFixed(1)}" y2="${p2.y.toFixed(1)}"
              stroke="${accent}" stroke-width="0.8" opacity="${i % 3 === 0 ? 0.3 : 0.14}"/>`;
  }).join("");

  // Rotation-scale markers around the ellipse: the "method cycle" reading.
  const markers = Array.from({ length: 9 }, (_, i) => {
    const a = (i / 9) * 360;
    const outer = polar(cx, cy, 1, a);
    const x = cx + (outer.x - cx) * (rx + 16);
    const y = cy + (outer.y - cy) * (ry + 8);
    const r = i === 0 ? 4.2 : 2.6;
    return `<circle class="orbit-marker" data-marker-index="${i}" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r}"
              fill="${i === 0 ? secondary : accent}" opacity="${i === 0 ? 0.95 : 0.55}"/>`;
  }).join("");

  return `
<svg class="scene scene--orbit" viewBox="0 0 ${width} ${height}" aria-hidden="true" focusable="false">
  ${sceneDefs(id, accent, secondary)}

  <!-- Ground plane grid, giving the ring a place to sit -->
  <g class="orbit-grid" opacity="0.4">
    <ellipse cx="${cx}" cy="${cy + ry + 58}" rx="${rx * 0.86}" ry="${ry * 0.34}"
             fill="none" stroke="${accent}" stroke-width="0.7" opacity="0.22"/>
    <ellipse cx="${cx}" cy="${cy + ry + 58}" rx="${rx * 0.62}" ry="${ry * 0.24}"
             fill="none" stroke="${accent}" stroke-width="0.6" opacity="0.14"/>
  </g>

  <!-- Radial spokes -->
  <g class="orbit-spokes">${spokeLines}</g>

  <!-- The primary tilted orbit -->
  <g class="orbit-tracks" fill="none">
    <ellipse class="orbit-track" cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}"
             stroke="url(#${id}-stroke)" stroke-width="2.4"/>
    <ellipse class="orbit-track" cx="${cx}" cy="${cy}" rx="${rx - 20}" ry="${ry - 8}"
             stroke="${accent}" stroke-width="0.6" opacity="0.35" stroke-dasharray="3 8"/>
    <ellipse class="orbit-track" cx="${cx}" cy="${cy}" rx="${rx + 22}" ry="${ry + 9}"
             stroke="${secondary}" stroke-width="0.5" opacity="0.18"/>
  </g>

  <!-- Phase position markers -->
  <g class="orbit-markers">${markers}</g>

  <!-- Ground shadow: anchors the orbit in space -->
  <ellipse cx="${cx}" cy="${cy + ry + 66}" rx="${rx * 0.7}" ry="28"
           fill="${secondary}" opacity="0.09" filter="url(#${id}-glow)"/>
</svg>`.trim();
}

/* ========================================================================== */
/* 3. The AI capability stack (isometric planes)                              */
/* ========================================================================== */

/**
 * An isometric stack of translucent planes: the six layers of the AI capability
 * layer, with a governance spine running alongside.
 *
 * Each layer is drawn as a rhombus top plus two shaded faces so the stack reads
 * as three-dimensional, and every name is written on its own top face.
 *
 * **The stack is drawn in data order, top to bottom**, so it reads in the same
 * order as the list of layers beside it. The planes still broaden a little
 * towards the bottom, which keeps the visual weight where a foundation belongs
 * without contradicting the list.
 *
 * @param {Object} [options]
 * @param {Array<{name: string, short: string, accent: string}>} [options.layers]
 * @param {string} [options.governanceAccent="#e0b584"]
 * @param {string} [options.governanceLabel="Governance"]
 * @returns {string} SVG markup.
 */
export function aiStack(options = {}) {
  const {
    layers = [],
    governanceAccent = "#e0b584",
    governanceLabel = "Governance",
  } = options;

  const id = uniqueId("stack");
  const width = 720;
  /** Vertical distance between planes — generous, so the stack has presence. */
  const stepY = 105;
  const topPad = 72;
  const halfHTop = 46;
  const halfHBottom = 54;
  const halfWTop = 232;
  const halfWBottom = 262;
  const cx = 300;

  /*
   * The viewBox is derived from the geometry rather than guessed: the top vertex
   * of the first plane sits at `topPad`, and the deepest side face of the last
   * plane extends `halfHBottom + 26` below its centre.
   */
  const total = Math.max(layers.length, 1);
  const span = (total - 1) * stepY;
  const baseY = topPad + span;
  const height = baseY + halfHBottom + 26 + 26;

  /*
   * Emitted in array order, which is also the order the list beside it uses:
   * first plane at the top, last at the bottom. Painting the bottom plane last
   * is also the right depth cue — it is nearest the viewer, so it has to be able
   * to occlude the planes above it.
   */
  const planes = layers
    .map((layer, i) => {
      // `t` runs 0 at the top plane to 1 at the bottom one, so the slight taper
      // is described once and both the width and the height follow it.
      const t = total === 1 ? 0 : i / (total - 1);
      const y = topPad + i * stepY;
      const halfW = halfWTop + (halfWBottom - halfWTop) * t;
      const halfH = halfHTop + (halfHBottom - halfHTop) * t;

      const top = `${cx - halfW},${y} ${cx},${y - halfH} ${cx + halfW},${y} ${cx},${y + halfH}`;
      const left = `${cx - halfW},${y} ${cx},${y + halfH} ${cx},${y + halfH + 26} ${cx - halfW},${y + 26}`;
      const right = `${cx + halfW},${y} ${cx},${y + halfH} ${cx},${y + halfH + 26} ${cx + halfW},${y + 26}`;

      return `
      <g class="stack-layer" data-layer-index="${i}"
         style="--layer-color:${layer.accent}">
        <polygon points="${left}"  fill="${layer.accent}" fill-opacity="0.14"/>
        <polygon points="${right}" fill="${layer.accent}" fill-opacity="0.08"/>
        <polygon points="${top}"   fill="${layer.accent}" fill-opacity="${(0.16 + t * 0.1).toFixed(2)}"
                 stroke="${layer.accent}" stroke-opacity="0.7" stroke-width="1.1"/>
        <text x="${cx}" y="${y + 6}" text-anchor="middle" class="stack-label"
              font-size="15" font-weight="600" fill="#f4f1ea">${escapeAttribute(layer.name)}</text>
      </g>`;
    })
    .join("");

  // The governance spine: a vertical post with a rung through every plane.
  const spineX = 640;
  const rungs = layers
    .map((layer, i) => {
      const y = topPad + i * stepY;
      return `<line x1="${spineX - 28}" y1="${y}" x2="${spineX}" y2="${y}"
                stroke="${governanceAccent}" stroke-width="0.9" opacity="0.5"/>`;
    })
    .join("");

  return `
<svg class="scene scene--stack" viewBox="0 0 ${width} ${height}" role="img"
     aria-label="An isometric stack of the six AI capability layers, listed from the data foundation down to business capability, with governance running alongside all of them.">
  ${sceneDefs(id, "#5fd4c4", "#d8a657")}
  <g class="stack-planes">${planes}</g>

  <!-- Governance spine -->
  <g class="stack-spine">
    ${rungs}
    <line x1="${spineX}" y1="${topPad - 36}" x2="${spineX}" y2="${baseY + 34}"
          stroke="${governanceAccent}" stroke-opacity="0.55" stroke-width="2.4" stroke-linecap="round"/>
    <text x="${spineX}" y="${topPad - 54}" text-anchor="middle"
          font-size="13" letter-spacing="2.4" fill="${governanceAccent}"
          style="text-transform:uppercase">${escapeAttribute(governanceLabel)}</text>
  </g>
</svg>`.trim();
}

/* ========================================================================== */
/* 4. Agentic orchestration loop                                              */
/* ========================================================================== */

/**
 * The agent orchestration loop: a central orchestrator with role satellites
 * arranged on a ring, joined by directed connectors, and a human review gate.
 *
 * @param {Object} [options]
 * @param {string[]} [options.steps]  Loop step labels.
 * @param {Array<{id: string, name: string, glyph: string}>} [options.agents]
 * @param {string} [options.accent="#5fd4c4"]
 * @param {string} [options.secondary="#d8a657"]
 * @param {number} [options.size=560]
 * @returns {string} SVG markup.
 */
export function agentLoop(options = {}) {
  const {
    steps = ["Observe", "Retrieve", "Reason", "Act", "Review"],
    agents = [],
    accent = "#5fd4c4",
    secondary = "#d8a657",
    size = 560,
  } = options;

  const id = uniqueId("agentloop");
  const c = size / 2;

  // Central orchestrator.
  const centreRad = 78;

  // Step ring.
  const ringRadius = 172;
  const stepNodes = steps
    .map((step, i) => {
      const angle = (360 / steps.length) * i;
      const p = polar(c, c, ringRadius, angle);
      const outer = polar(c, c, ringRadius + 27, angle);
      return `
      <g class="loop-step" data-step-index="${i}">
        <circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="27"
                fill="#12141a" stroke="${accent}" stroke-width="1.5" stroke-opacity="0.75"/>
        <text x="${p.x.toFixed(1)}" y="${p.y.toFixed(1)}" text-anchor="middle" dominant-baseline="central"
              font-size="11.5" font-weight="600" fill="#f4f1ea">${escapeAttribute(step)}</text>
        <circle class="loop-pulse" cx="${outer.x.toFixed(1)}" cy="${outer.y.toFixed(1)}" r="3"
                fill="${secondary}" opacity="0.75"/>
      </g>`;
    })
    .join("");

  // Connectors between the steps (the directed cycle).
  const connectors = steps
    .map((_, i) => {
      const a1 = (360 / steps.length) * i;
      const a2 = (360 / steps.length) * ((i + 1) % steps.length);
      return `<path class="loop-connector" d="${arcPath(c, c, ringRadius, a1 + 12, a2 - 12)}"
                fill="none" stroke="${accent}" stroke-width="1" stroke-opacity="0.32"
                marker-end="url(#${id}-arrow)"/>`;
    })
    .join("");

  // Agent satellites further out.
  const satellites = agents
    .map((agent, i) => {
      const angle = (360 / Math.max(agents.length, 1)) * i + 22;
      const p = polar(c, c, 250, angle);
      const glyph = GLYPH_DATA[agent.glyph] ? agent.glyph : "spark";
      return `
      <g class="loop-agent" data-agent-id="${agent.id}">
        <line x1="${polar(c, c, ringRadius + 30, angle).x.toFixed(1)}" y1="${polar(c, c, ringRadius + 30, angle).y.toFixed(1)}"
              x2="${polar(c, c, 232, angle).x.toFixed(1)}" y2="${polar(c, c, 232, angle).y.toFixed(1)}"
              stroke="${secondary}" stroke-width="0.8" stroke-opacity="0.35" stroke-dasharray="3 5"/>
        <g transform="translate(${(p.x - 15).toFixed(1)} ${(p.y - 15).toFixed(1)}) scale(1.25)"
           stroke="${secondary}" stroke-width="1.3" fill="none" stroke-linecap="round" stroke-linejoin="round"
           opacity="0.9">${GLYPH_DATA[glyph]}</g>
      </g>`;
    })
    .join("");

  return `
<svg class="scene scene--loop" viewBox="0 0 ${size} ${size}" role="img"
     aria-label="The agentic orchestration loop: Observe, Retrieve, Reason, Act and Review arranged around a central orchestrator, with role-based agents orbiting outside the loop.">
  ${sceneDefs(id, accent, secondary)}

  <circle cx="${c}" cy="${c}" r="${ringRadius}" fill="none" stroke="${accent}" stroke-width="0.7" stroke-opacity="0.18" stroke-dasharray="2 8"/>
  <circle cx="${c}" cy="${c}" r="250" fill="none" stroke="${secondary}" stroke-width="0.6" stroke-opacity="0.14"/>
  <g class="loop-connectors">${connectors}</g>
  <g class="loop-steps">${stepNodes}</g>
  <g class="loop-agents">${satellites}</g>

  <!-- Central orchestrator -->
  <g class="loop-core" style="transform-origin:${c}px ${c}px">
    <circle cx="${c}" cy="${c}" r="${centreRad + 26}" fill="url(#${id}-halo)" opacity="0.5"/>
    <path d="${polygonPath(c, c, centreRad, 6, 0)}" fill="rgba(18,20,26,0.85)"
          stroke="url(#${id}-stroke)" stroke-width="1.8"/>
    <path d="${polygonPath(c, c, centreRad - 18, 6, 30)}" fill="none"
          stroke="${accent}" stroke-width="0.8" stroke-opacity="0.5"/>
    <text x="${c}" y="${c - 8}" text-anchor="middle" font-size="11" letter-spacing="2.6"
          fill="${accent}" style="text-transform:uppercase">Orchestrator</text>
    <text x="${c}" y="${c + 12}" text-anchor="middle" font-size="15" font-weight="600"
          fill="#f4f1ea">Human review</text>
  </g>
</svg>`.trim();
}

/* ========================================================================== */
/* 5. Capability web                                                          */
/* ========================================================================== */

/**
 * A radial capability web: the ADM hub at the centre, capability groups as
 * concentric arcs and individual capabilities as nodes on the spokes.
 *
 * @param {Object} [options]
 * @param {Array<{id: string, name: string, skip?: boolean}>} [options.nodes]
 * @param {string[]} [options.groups]     Group labels, inner → outer.
 * @param {string} [options.accent="#5fd4c4"]
 * @param {string} [options.secondary="#d8a657"]
 * @param {number} [options.size=600]
 * @returns {string} SVG markup.
 */
export function capabilityWeb(options = {}) {
  const { nodes = [], groups = [], accent = "#5fd4c4", secondary = "#d8a657", size = 600 } = options;
  const id = uniqueId("web");
  const c = size / 2;

  const rings = groups
    .map((label, i) => {
      const r = 108 + i * 68;
      return `<circle class="web-ring" cx="${c}" cy="${c}" r="${r}" fill="none"
                stroke="${i % 2 === 0 ? accent : secondary}" stroke-width="0.7"
                stroke-opacity="${0.26 - i * 0.05}" stroke-dasharray="${i === 0 ? "none" : "2 7"}"/>
              <text x="${c}" y="${c - r + 15}" text-anchor="middle" font-size="10"
                    letter-spacing="2.2" fill="${i % 2 === 0 ? accent : secondary}"
                    opacity="0.6" style="text-transform:uppercase">${escapeAttribute(label)}</text>`;
    })
    .join("");

  const spokes = nodes
    .map((node, i) => {
      const angle = (360 / Math.max(nodes.length, 1)) * i;
      const r = 176 + (i % 3) * 34;
      const p = polar(c, c, r, angle);
      const inner = polar(c, c, 74, angle);
      return `
      <line x1="${inner.x.toFixed(1)}" y1="${inner.y.toFixed(1)}" x2="${p.x.toFixed(1)}" y2="${p.y.toFixed(1)}"
            stroke="${accent}" stroke-width="0.6" stroke-opacity="0.2"/>
      <g class="web-node" data-web-node="${node.id}" style="transform-origin:${p.x.toFixed(1)}px ${p.y.toFixed(1)}px">
        <circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="17"
                fill="#12141a" stroke="${secondary}" stroke-width="1.2" stroke-opacity="0.7"/>
        <circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="4.2" fill="${accent}"/>
        <text x="${p.x.toFixed(1)}" y="${(p.y + 33).toFixed(1)}" text-anchor="middle"
              font-size="10.5" fill="#cfc9be">${escapeAttribute(node.name)}</text>
      </g>`;
    })
    .join("");

  return `
<svg class="scene scene--web" viewBox="0 0 ${size} ${size}" role="img"
     aria-label="A radial capability map: twelve enterprise capabilities arranged on spokes around the architecture method hub, grouped into design, deliver and sustain.">
  ${sceneDefs(id, accent, secondary)}
  <g class="web-rings">${rings}</g>
  <g class="web-spokes">${spokes}</g>
  <g class="web-core" style="transform-origin:${c}px ${c}px">
    <circle cx="${c}" cy="${c}" r="62" fill="url(#${id}-core)" opacity="0.5"/>
    <path d="${polygonPath(c, c, 46, 6, 0)}" fill="rgba(18,20,26,0.8)" stroke="url(#${id}-stroke)" stroke-width="1.6"/>
    <text x="${c}" y="${c - 4}" text-anchor="middle" font-size="11.5" font-weight="600" fill="#f4f1ea">TOGAF</text>
    <text x="${c}" y="${c + 13}" text-anchor="middle" font-size="11.5" font-weight="600" fill="${accent}">ADM</text>
  </g>
</svg>`.trim();
}

/* ========================================================================== */
/* 6. Trend radar                                                             */
/* ========================================================================== */

/**
 * The technology radar: four concentric rings, four quadrant axes and a labelled
 * grid. Trend dots are plotted by `components/trends.js` from the data module,
 * so the artwork and the content stay independent.
 *
 * @param {Object} [options]
 * @param {Array<{id: string, label: string}>} [options.rings]
 * @param {string[]} [options.quadrants]
 * @param {string} [options.accent="#5fd4c4"]
 * @param {string} [options.secondary="#d8a657"]
 * @param {number} [options.size=560]
 * @returns {string} SVG markup.
 */
export function trendRadar(options = {}) {
  const { rings = [], quadrants = [], accent = "#5fd4c4", secondary = "#d8a657", size = 620 } = options;
  const id = uniqueId("radar");
  const c = size / 2;
  const maxR = Math.round(size * 0.4214);

  const ringCircles = rings
    .map((ring) => {
      const r = maxR * ring.radius;
      return `<circle class="radar-ring" data-ring="${ring.id}" cx="${c}" cy="${c}" r="${r.toFixed(1)}"
                fill="none" stroke="${ring.id === "adopt" ? accent : secondary}"
                stroke-width="${ring.id === "adopt" ? 1.1 : 0.7}"
                stroke-opacity="${0.34 - (1 - ring.radius) * 0.16}"
                stroke-dasharray="${ring.id === "adopt" ? "none" : "3 7"}"/>
              <text x="${c + 6}" y="${(c - r + 15).toFixed(1)}" font-size="10"
                    letter-spacing="2.4" fill="${ring.id === "adopt" ? accent : secondary}"
                    opacity="0.55" style="text-transform:uppercase">${escapeAttribute(ring.label)}</text>`;
    })
    .join("");

  const axes = quadrants
    .map((label, i) => {
      const angle = (360 / quadrants.length) * i - 90 + 90 / quadrants.length;
      const p = polar(c, c, maxR + 4, angle);
      const outer = polar(c, c, maxR + 30, angle);
      const mid = polar(c, c, maxR + 48, angle);
      return `<line x1="${c}" y1="${c}" x2="${p.x.toFixed(1)}" y2="${p.y.toFixed(1)}"
                stroke="${secondary}" stroke-width="0.6" stroke-opacity="0.16"/>
              <line x1="${p.x.toFixed(1)}" y1="${p.y.toFixed(1)}" x2="${outer.x.toFixed(1)}" y2="${outer.y.toFixed(1)}"
                stroke="${secondary}" stroke-width="0.8" stroke-opacity="0.4"/>
              <text x="${mid.x.toFixed(1)}" y="${mid.y.toFixed(1)}" text-anchor="middle" dominant-baseline="central"
                    font-size="10.5" letter-spacing="1.6" fill="#cfc9be"
                    style="text-transform:uppercase">${escapeAttribute(label)}</text>`;
    })
    .join("");

  const crossAxes = `
    <line x1="${c - maxR}" y1="${c}" x2="${c + maxR}" y2="${c}" stroke="${secondary}" stroke-width="0.5" stroke-opacity="0.14"/>
    <line x1="${c}" y1="${c - maxR}" x2="${c}" y2="${c + maxR}" stroke="${secondary}" stroke-width="0.5" stroke-opacity="0.14"/>`;

  return `
<svg class="scene scene--radar" viewBox="0 0 ${size} ${size}" role="img"
     aria-label="A technology radar with four rings — Adopt, Trial, Assess and Hold — and four quadrants covering data, models, delivery and assurance.">
  ${sceneDefs(id, accent, secondary)}
  ${crossAxes}
  <g class="radar-rings">${ringCircles}</g>
  <g class="radar-axes">${axes}</g>
  <circle cx="${c}" cy="${c}" r="4" fill="${accent}"/>
  <g class="radar-plots" data-radar-plots></g>
</svg>`.trim();
}

/* ========================================================================== */
/* 7. The AI dot constellation                                                */
/* ========================================================================== */

/**
 * The "AI dots": points of intelligence distributed across four planes of the
 * estate — cloud, workflow, edge and device. Faint connection lines trace the
 * paths between them, and a classification legend anchors the picture.
 *
 * @param {Object} [options]
 * @param {Array<{id: string, label: string, where: string}>} [options.dots]
 * @param {string} [options.accent="#5fd4c4"]
 * @param {string} [options.secondary="#d8a657"]
 * @param {number} [options.width=860]
 * @param {number} [options.height=520]
 * @returns {string} SVG markup.
 */
export function dotConstellation(options = {}) {
  const { dots = [], accent = "#5fd4c4", secondary = "#d8a657", width = 860, height = 520 } = options;
  const id = uniqueId("dots");

  // Four horizontal planes, cloud at the top down to device at the bottom.
  const planes = [
    { id: "cloud", label: "Cloud", y: 96, color: accent },
    { id: "workflow", label: "Workflow", y: 214, color: secondary },
    { id: "edge", label: "Edge", y: 332, color: "#a98bc4" },
    { id: "device", label: "Device", y: 442, color: "#9db08a" },
  ];

  const planeBands = planes
    .map(
      (plane) => `
      <g class="dot-plane" data-plane="${plane.id}">
        <line x1="70" y1="${plane.y}" x2="${width - 52}" y2="${plane.y}"
              stroke="${plane.color}" stroke-width="0.7" stroke-opacity="0.22" stroke-dasharray="2 9"/>
        <text x="70" y="${plane.y - 14}" font-size="10.5" letter-spacing="2.4"
              fill="${plane.color}" opacity="0.7" style="text-transform:uppercase">${escapeAttribute(plane.label)}</text>
      </g>`
    )
    .join("");

  // Place each dot on its plane; spread them across the width deterministically.
  const placed = dots.map((dot, i) => {
    const plane = planes.find((p) => p.id.toLowerCase() === dot.where.toLowerCase()) ?? planes[0];
    const samePlane = dots.filter((d) => d.where.toLowerCase() === plane.id);
    const positionInPlane = samePlane.findIndex((d) => d.id === dot.id);
    const spread = samePlane.length > 1 ? positionInPlane / (samePlane.length - 1) : 0.5;
    const x = 150 + spread * (width - 260) + (jitter(i) - 0.5) * 26;
    const y = plane.y - 6 - jitter(i + 4) * 22;
    return { dot, x, y, plane, index: i };
  });

  const links = placed
    .map((a, i) => {
      const b = placed[(i + 3) % placed.length];
      if (!b || a.plane.id === b.plane.id) return "";
      return `<line class="dot-link" x1="${a.x.toFixed(1)}" y1="${a.y.toFixed(1)}"
                x2="${b.x.toFixed(1)}" y2="${b.y.toFixed(1)}"
                stroke="${a.plane.color}" stroke-width="0.6" stroke-opacity="0.14"/>`;
    })
    .join("");

  const nodes = placed
    .map(
      ({ dot, x, y, plane, index }) => `
      <g class="dot-node" data-dot-id="${dot.id}" style="transform-origin:${x.toFixed(1)}px ${y.toFixed(1)}px">
        <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${(9 + (index % 3) * 2.5).toFixed(1)}"
                fill="${plane.color}" fill-opacity="0.12" stroke="${plane.color}" stroke-width="1"
                stroke-opacity="0.6"/>
        <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3.4" fill="${plane.color}"/>
      </g>`
    )
    .join("");

  return `
<svg class="scene scene--dots" viewBox="0 0 ${width} ${height}" role="img"
     aria-label="The AI dot constellation: points of intelligence distributed across four planes of the estate — cloud, workflow, edge and device — connected by faint pathways.">
  ${sceneDefs(id, accent, secondary)}
  <g class="dot-planes">${planeBands}</g>
  <g class="dot-links">${links}</g>
  <g class="dot-nodes">${nodes}</g>
</svg>`.trim();
}

/* ========================================================================== */
/* 8. Governance lattice                                                      */
/* ========================================================================== */

/**
 * A governance lattice: two sets of orthogonal lines with decision nodes at the
 * intersections, and four forum plates across the top. Reads as structure —
 * the thing governance actually is.
 *
 * @param {Object} [options]
 * @param {Array<{id: string, name: string}>} [options.forums]
 * @param {string} [options.accent="#5fd4c4"]
 * @param {string} [options.secondary="#d8a657"]
 * @param {number} [options.width=900]
 * @param {number} [options.height=440]
 * @returns {string} SVG markup.
 */
export function governanceLattice(options = {}) {
  const { forums = [], accent = "#5fd4c4", secondary = "#d8a657", width = 900, height = 440 } = options;
  const id = uniqueId("lattice");

  const cols = 12;
  const rows = 6;
  const left = 90;
  const right = width - 60;
  const top = 150;
  const bottom = height - 40;

  /*
   * Forum plates.
   *
   * These are the four forums, and they sit directly above the four cards that
   * describe them further down the page, so they are drawn in the same card
   * language: the token surface and border, a 24px-equivalent radius, real
   * padding, and the display serif for the label.
   *
   * Earlier this was HTML inside a `foreignObject`, which inherited the page's
   * text style and ended up in a different typeface at a size that did not
   * belong to the type scale, clipped at two lines. Text is now real SVG text:
   * no measurement is needed to centre it, it scales like the rest of the
   * scene, and it cannot overflow its plate.
   */
  const plateY = 40;
  const plateH = 68; // two label lines plus the card's padding, at 1.34× scale
  const plateRadius = 18; // ≈ 24 CSS px at the scene's usual rendered width
  const lineHeight = 18;
  const labelSize = 14; // user units

  /**
   * Break a forum name into lines that fit the plate.
   *
   * An SVG string has no font metrics to measure against, so this uses a
   * deliberately conservative estimate: Fraunces at this size advances roughly
   * half an em per character, and the plate's inner width holds about twenty of
   * them. Erring small keeps the label inside the plate, which is the failure
   * that would actually be visible.
   *
   * @param {string} name
   * @returns {string[]} One or more lines, in reading order.
   */
  const wrapLabel = (name) => {
    const budget = 20;
    const lines = [];
    let line = "";
    String(name)
      .split(/\s+/)
      .filter(Boolean)
      .forEach((word) => {
        const candidate = line ? `${line} ${word}` : word;
        if (line && candidate.length > budget) {
          lines.push(line);
          line = word;
        } else {
          line = candidate;
        }
      });
    if (line) lines.push(line);
    return lines;
  };

  const verticals = Array.from({ length: cols }, (_, i) => {
    const x = left + ((right - left) / (cols - 1)) * i;
    return `<line x1="${x.toFixed(1)}" y1="${top}" x2="${x.toFixed(1)}" y2="${bottom}"
              stroke="${accent}" stroke-width="0.6" stroke-opacity="${i % 3 === 0 ? 0.3 : 0.14}"/>`;
  }).join("");

  const horizontals = Array.from({ length: rows }, (_, i) => {
    const y = top + ((bottom - top) / (rows - 1)) * i;
    return `<line x1="${left}" y1="${y.toFixed(1)}" x2="${right}" y2="${y.toFixed(1)}"
              stroke="${secondary}" stroke-width="0.6" stroke-opacity="${i === 0 ? 0.36 : 0.16}"/>`;
  }).join("");

  const nodes = [];
  for (let r = 0; r < rows; r += 1) {
    for (let cIdx = 0; cIdx < cols; cIdx += 1) {
      if ((r + cIdx) % 4 !== 0) continue;
      const x = left + ((right - left) / (cols - 1)) * cIdx;
      const y = top + ((bottom - top) / (rows - 1)) * r;
      nodes.push(
        `<circle class="lattice-node" data-node="${r}-${cIdx}" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r === 0 ? 4.6 : 3.2}"
           fill="${r === 0 ? secondary : accent}" opacity="${r === 0 ? 0.9 : 0.5}"/>`
      );
    }
  }

  const plates = forums
    .map((forum, i) => {
      const w = (right - left) / forums.length - 14;
      const x = left + ((right - left) / forums.length) * i;
      const cx = x + w / 2;
      const lines = wrapLabel(forum.name);
      // Centre the block of lines on the plate, then nudge for the fact that a
      // baseline sits below the optical middle of a line.
      const first = plateY + plateH / 2 - ((lines.length - 1) * lineHeight) / 2 + labelSize * 0.34;
      const label = lines
        .map(
          (line, index) =>
            `<tspan x="${cx.toFixed(1)}" y="${(first + index * lineHeight).toFixed(1)}">${escapeAttribute(line)}</tspan>`
        )
        .join("");
      return `
      <g class="lattice-plate" data-forum-id="${forum.id}">
        <rect class="lattice-plate__box" x="${x.toFixed(1)}" y="${plateY}"
              width="${w.toFixed(1)}" height="${plateH}" rx="${plateRadius}"/>
        <line x1="${cx.toFixed(1)}" y1="${plateY + plateH}" x2="${cx.toFixed(1)}" y2="${top}"
              stroke="${secondary}" stroke-width="0.7" stroke-opacity="0.3" stroke-dasharray="3 5"/>
        <text class="lattice-plate__label" text-anchor="middle" font-size="${labelSize}">${label}</text>
      </g>`;
    })
    .join("");

  return `
<svg class="scene scene--lattice" viewBox="0 0 ${width} ${height}" role="img"
     aria-label="A governance lattice: four decision forums at the top, connected through a grid of decision points that represent the cadence of architecture and AI governance.">
  ${sceneDefs(id, accent, secondary)}
  <g class="lattice-plates">${plates}</g>
  <g class="lattice-grid">${verticals}${horizontals}</g>
  <g class="lattice-nodes">${nodes.join("")}</g>
</svg>`.trim();
}

/* ========================================================================== */
/* 9. Transformation river (journey backdrop)                                 */
/* ========================================================================== */

/**
 * A flowing multi-lane path used as the backdrop of the journey section: nine
 * lanes, one per ADM phase, converging toward a single outcome.
 *
 * @param {Object} [options]
 * @param {number} [options.lanes=9]
 * @param {string} [options.accent="#5fd4c4"]
 * @param {string} [options.secondary="#d8a657"]
 * @param {number} [options.width=1600]
 * @param {number} [options.height=600]
 * @returns {string} SVG markup.
 */
export function transformationRiver(options = {}) {
  const { lanes = 9, accent = "#5fd4c4", secondary = "#d8a657", width = 1600, height = 600 } = options;
  const id = uniqueId("river");

  const paths = Array.from({ length: lanes }, (_, i) => {
    const t = lanes === 1 ? 0.5 : i / (lanes - 1);
    const startY = 60 + t * (height - 120);
    const endY = height / 2 + (t - 0.5) * 90;
    const mid1 = 0.34;
    const mid2 = 0.7;
    const wobble = (jitter(i) - 0.5) * 90;
    const d = `M -40 ${startY.toFixed(1)}
               C ${(width * mid1).toFixed(0)} ${(startY + wobble).toFixed(1)},
                 ${(width * mid2).toFixed(0)} ${(endY - wobble).toFixed(1)},
                 ${(width + 40).toFixed(0)} ${endY.toFixed(1)}`;
    const color = i % 2 === 0 ? accent : secondary;
    return `<path class="river-lane" data-lane="${i}" d="${d}" fill="none"
              stroke="${color}" stroke-width="${(1.4 - t * 0.5).toFixed(2)}"
              stroke-opacity="${(0.32 - t * 0.12).toFixed(2)}" stroke-linecap="round"/>`;
  }).join("");

  return `
<svg class="scene scene--river" viewBox="0 0 ${width} ${height}" aria-hidden="true" focusable="false"
     preserveAspectRatio="xMidYMid slice">
  ${sceneDefs(id, accent, secondary)}
  <g class="river-lanes">${paths}</g>
</svg>`.trim();
}
